import {Box, Button, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {CloseIcon} from '../icons'
import Cookies from 'js-cookie'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGetCountrySelector} from '../../custom-api/hooks/use-get-country-selector'
import useCountries from '../../hooks/use-countries'
import {handleLocale} from '../../utils/locale'
import Field from '../field'
import CountryFlag from './sub-components/CountryFlag'

const CountrySelectorBanner = ({isHidden}) => {
    const style = useMultiStyleConfig('CountrySelector')
    const {locale, buildUrl} = useMultiSite()
    const [isVisible, setIsVisible] = useState(false)
    const localeAlias = locale?.alias || null
    const intl = useIntl()
    const {data} = useGetCountrySelector()
    const countries = useCountries(data)

    const countryOptions = useMemo(
        () =>
            (countries || [])
                .flatMap((region) =>
                    Object.entries(region?.countries || {}).flatMap(([countryCode, locales]) =>
                        locales.map((countryLocale) => ({
                            label: countryLocale.country,
                            value: countryLocale.url
                                ?.split('/')
                                .filter(Boolean)
                                .pop()
                                ?.toLowerCase(),
                            locale: countryLocale,
                            countryCode,
                        })),
                    ),
                )
                .filter((option) => option.value),
        [countries],
    )

    const {control, watch, setValue} = useForm({
        defaultValues: {countryLocale: localeAlias || ''},
    })
    const hasInitializedCountry = useRef(false)

    const selectedLocale = watch('countryLocale')

    const selectedLocaleAlias =
        typeof selectedLocale === 'string' ? selectedLocale : selectedLocale?.value

    const selectedCountry = countryOptions.find((option) => option.value === selectedLocaleAlias)

    useEffect(() => {
        const countrySelectorCookie = Cookies.get('cdn-country')
        const dlgCountrySelectorCookie = Cookies.get('dlg-country')
        const dlgMatchesIpCookie = dlgCountrySelectorCookie === countrySelectorCookie
        if (localeAlias) {
            if (countrySelectorCookie && countrySelectorCookie !== localeAlias) {
                //rules R9 R10 R11 R12
                setIsVisible(true)
                if (!dlgCountrySelectorCookie || dlgCountrySelectorCookie !== localeAlias) {
                    Cookies.set('dlg-country', localeAlias, {expires: 30})
                }
            } else if (
                //rulse R6 and R8
                (!countrySelectorCookie && !dlgCountrySelectorCookie) ||
                (localeAlias === countrySelectorCookie && dlgMatchesIpCookie)
            ) {
                setIsVisible(false)
            } else if (
                //rules R7 and R8b
                localeAlias === countrySelectorCookie &&
                !dlgMatchesIpCookie
            ) {
                Cookies.set('dlg-country', localeAlias, {expires: 30})
            }
        }
    }, [])

    useEffect(() => {
        if (hasInitializedCountry.current) return

        const cookieLocale = Cookies.get('cdn-country')?.toLowerCase()

        if (countryOptions.some((option) => option.value === cookieLocale)) {
            hasInitializedCountry.current = true
            setValue('countryLocale', cookieLocale)
        }
    }, [countryOptions, setValue])

    const handleDismiss = () => {
        Cookies.set('dlg-country', localeAlias, {expires: 30})
        setIsVisible(false)
    }

    if (!isVisible || isHidden) {
        return null
    }

    return (
        <Flex {...style.container}>
            <Flex {...style.content}>
                <Flex {...style.labelContainer} {...style.hideOnDesktop}>
                    <Text {...style.label}>
                        <FormattedMessage
                            id="country_selector.label_choose_country"
                            defaultMessage="Choose your preferred country to view local content and get a better experience."
                        />
                    </Text>

                    <Button
                        variant="ghost-not-rounded"
                        onClick={handleDismiss}
                        aria-label={intl.formatMessage({
                            id: 'country.banner.close',
                            defaultMessage: 'Close',
                        })}
                        {...style.closeIconContainer}
                        {...style.hideOnDesktop}
                    >
                        <CloseIcon {...style.closeIcon} />
                    </Button>
                </Flex>
                <Text {...style.label} {...style.hideOnMobile}>
                    <FormattedMessage
                        id="country_selector.label_choose_country"
                        defaultMessage="Choose your preferred country to view local content and get a better experience."
                    />
                </Text>

                <Flex {...style.ctaContainer}>
                    <Box {...style?.inputContainer}>
                        <Field
                            name="countryLocale"
                            type="select"
                            control={control}
                            hidePlaceholder
                            leftIcon={(option) => <CountryFlag countryCode={option?.countryCode} />}
                            ariaLabel={intl.formatMessage({
                                id: 'country_selector.select_country',
                                defaultMessage: 'Select a country',
                            })}
                            options={countryOptions}
                            size="base"
                        />
                    </Box>

                    <Button
                        data-testid="go-to-country-button"
                        {...style.blueButton}
                        variant="primary-not-rounded"
                        width="auto"
                        flexShrink={0}
                        isDisabled={!selectedCountry}
                        onClick={(event) =>
                            handleLocale(
                                event,
                                selectedCountry?.locale,
                                buildUrl,
                                selectedCountry?.value,
                            )
                        }
                    >
                        <Text {...style.blueButtonText}>
                            <FormattedMessage
                                id="country_selector.button_continue"
                                defaultMessage="Continue"
                            />
                        </Text>
                    </Button>
                </Flex>

                <Button
                    variant="ghost-not-rounded"
                    {...style.closeIconContainer}
                    {...style.hideOnMobile}
                >
                    <CloseIcon
                        {...style.closeIcon}
                        onClick={handleDismiss}
                        aria-label={intl.formatMessage({
                            id: 'country.banner.close',
                            defaultMessage: 'Close',
                        })}
                    />
                </Button>
            </Flex>
        </Flex>
    )
}

CountrySelectorBanner.propTypes = {isHidden: PropTypes.bool}

export default CountrySelectorBanner
